/**
 * SocialProofBadge Component
 *
 * A flexible social proof badge that displays various types of trust signals.
 * Supports both automatic formatting (for numbers) and custom pre-formatted text.
 *
 * Design Pattern: Flexible Formatting
 * - Numbers are automatically formatted using type-specific templates
 * - Pre-formatted strings (containing text/symbols) are displayed as-is
 * - This prevents double-formatting issues while maintaining flexibility
 *
 * @example
 * // Automatic formatting from number
 * <SocialProofBadge type="popularity" value={68} />
 * // Output: "68 booked this week"
 *
 * @example
 * // Pre-formatted custom text
 * <SocialProofBadge type="popularity" value="68% choose this" />
 * // Output: "68% choose this"
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Users, TrendingUp, Shield, Star, Clock, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ProofType = 'popularity' | 'trending' | 'verified' | 'rating' | 'recent' | 'trusted';

interface SocialProofBadgeProps {
  type: ProofType;
  /**
   * The value to display in the badge.
   * - If a number: Will be formatted using the defaultText function for the proof type
   * - If a string with text/symbols: Will be displayed as-is (pre-formatted)
   * - If a string with only numbers: Will be passed to defaultText for formatting
   * Examples:
   *   value={68} → "68 booked this week" (for popularity type)
   *   value="68% choose this" → "68% choose this" (displayed as-is)
   *   value="68" → "68 booked this week" (formatted)
   */
  value?: string | number;
  variant?: 'default' | 'subtle' | 'prominent';
  showTooltip?: boolean;
  tooltipContent?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

// Configuration for different proof types
const proofConfig: Record<ProofType, {
  icon: React.ComponentType<{ className?: string }>;
  defaultText: (value?: string | number) => string;
  tooltipText: string;
  colorClass: string;
}> = {
  popularity: {
    icon: Users,
    defaultText: (value) => value ? `${value} booked this week` : 'Popular choice',
    tooltipText: 'Based on recent customer bookings in your area',
    colorClass: 'text-blue-600 bg-blue-50 border-blue-200',
  },
  trending: {
    icon: TrendingUp,
    defaultText: (value) => value ? `${value}% more popular` : 'Trending',
    tooltipText: 'Increasing in popularity over the last month',
    colorClass: 'text-green-600 bg-green-50 border-green-200',
  },
  verified: {
    icon: Shield,
    defaultText: () => 'Verified provider',
    tooltipText: 'Licensed and insured waste management provider',
    colorClass: 'text-purple-600 bg-purple-50 border-purple-200',
  },
  rating: {
    icon: Star,
    defaultText: (value) => value ? `${value} stars` : 'Top rated',
    tooltipText: 'Average customer rating from verified reviews',
    colorClass: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  },
  recent: {
    icon: Clock,
    defaultText: (value) => value ? `${value} booked today` : 'Recently booked',
    tooltipText: 'Other customers have booked this recently',
    colorClass: 'text-gray-600 bg-gray-50 border-gray-200',
  },
  trusted: {
    icon: CheckCircle2,
    defaultText: (value) => value ? `Trusted by ${value}+ customers` : 'Trusted choice',
    tooltipText: 'Highly recommended by our customers',
    colorClass: 'text-indigo-600 bg-indigo-50 border-indigo-200',
  },
};

// Size configurations
const sizeConfig = {
  sm: {
    badge: 'h-6 text-xs px-2 py-0',
    icon: 'w-3 h-3',
    gap: 'gap-1',
  },
  md: {
    badge: 'h-7 text-sm px-2.5 py-0.5',
    icon: 'w-3.5 h-3.5',
    gap: 'gap-1.5',
  },
  lg: {
    badge: 'h-8 text-base px-3 py-1',
    icon: 'w-4 h-4',
    gap: 'gap-2',
  },
};

// Variant styles
const variantStyles = {
  default: '',
  subtle: 'bg-opacity-50 border-opacity-30',
  prominent: 'shadow-sm ring-1 ring-offset-1',
};

export const SocialProofBadge: React.FC<SocialProofBadgeProps> = ({
  type,
  value,
  variant = 'default',
  showTooltip = true,
  tooltipContent,
  className,
  size = 'md',
}) => {
  const config = proofConfig[type];
  const Icon = config.icon;

  // Smart text formatting:
  // - If value is a number, always use defaultText formatter
  // - If value is a string that looks pre-formatted (contains non-numeric text), use as-is
  // - Otherwise, pass to defaultText for formatting
  const getText = () => {
    if (typeof value === 'number') {
      return config.defaultText(value);
    }
    if (typeof value === 'string') {
      // Check if the string contains more than just numbers and basic punctuation
      // This indicates it's a pre-formatted message like "68% choose this"
      const isPreFormatted = /[a-zA-Z%]/.test(value);
      if (isPreFormatted) {
        return value; // Use the string as-is
      }
    }
    // Fall back to default formatting
    return config.defaultText(value);
  };

  const text = getText();
  const tooltip = tooltipContent || config.tooltipText;
  const sizeClass = sizeConfig[size];

  // For now, tooltips are disabled until we add the tooltip component
  // The tooltip text is available in the config if we add it later
  return (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex items-center font-medium border',
        sizeClass.badge,
        sizeClass.gap,
        config.colorClass,
        variantStyles[variant],
        className
      )}
      title={showTooltip ? tooltip : undefined}
    >
      <Icon className={cn(sizeClass.icon, 'flex-shrink-0')} />
      <span>{text}</span>
    </Badge>
  );
};

// Composite badge for multiple proof points
interface SocialProofGroupProps {
  proofs: Array<{
    type: ProofType;
    value?: string | number;
  }>;
  layout?: 'horizontal' | 'vertical' | 'compact';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const SocialProofGroup: React.FC<SocialProofGroupProps> = ({
  proofs,
  layout = 'horizontal',
  size = 'sm',
  className,
}) => {
  const layoutClasses = {
    horizontal: 'flex flex-wrap gap-2',
    vertical: 'flex flex-col gap-2',
    compact: 'inline-flex gap-1',
  };

  // Limit to 3 most important proofs in compact mode
  const displayProofs = layout === 'compact' ? proofs.slice(0, 3) : proofs;

  return (
    <div className={cn(layoutClasses[layout], className)}>
      {displayProofs.map((proof, index) => (
        <SocialProofBadge
          key={`${proof.type}-${index}`}
          type={proof.type}
          value={proof.value}
          size={size}
          variant={layout === 'compact' ? 'subtle' : 'default'}
        />
      ))}
    </div>
  );
};

// Integration helper for existing card components
interface WithSocialProofProps {
  children: React.ReactNode;
  proofs?: Array<{
    type: ProofType;
    value?: string | number;
  }>;
  position?: 'top-right' | 'top-left' | 'bottom' | 'inline';
  className?: string;
}

export const WithSocialProof: React.FC<WithSocialProofProps> = ({
  children,
  proofs,
  position = 'top-right',
  className,
}) => {
  if (!proofs || proofs.length === 0) {
    return <>{children}</>;
  }

  const positionClasses = {
    'top-right': 'absolute top-2 right-2 z-10',
    'top-left': 'absolute top-2 left-2 z-10',
    'bottom': 'mt-2',
    'inline': '',
  };

  return (
    <div className={cn('relative', className)}>
      {position !== 'bottom' && children}
      <div className={cn(positionClasses[position])}>
        <SocialProofGroup
          proofs={proofs}
          layout={position === 'inline' ? 'compact' : 'horizontal'}
          size="sm"
        />
      </div>
      {position === 'bottom' && children}
    </div>
  );
};

export default SocialProofBadge;